import { z } from 'zod';

// ── Shared constraints ──────────────────────────────────────────────────────

/**
 * RFC 5321 cap on email address length.
 * Combines format + length in one reusable schema.
 */
const emailSchema = z
  .string()
  .email('Please enter a valid email address.')
  .max(254, 'Email address must be 254 characters or fewer.');

/**
 * Password requirements enforced on both server and client.
 * Better Auth also enforces min/max (8 / 128) independently.
 */
const passwordSchema = z
  .string()
  .min(8, 'Password must be at least 8 characters.')
  .max(128, 'Password must be 128 characters or fewer.')
  .regex(
    /^(?=.*[a-zA-Z])(?=.*\d)/,
    'Password must contain at least one letter and one number.',
  );

/**
 * Blocks `<` and `>` to prevent HTML injection in name fields that are
 * interpolated into email templates or rendered as text.  Allows all other
 * characters (including Unicode) so international names are supported.
 */
const nameSchema = (label: string, max: number) =>
  z
    .string()
    .min(1, `${label} is required.`)
    .max(max, `${label} must be ${max} characters or fewer.`)
    .regex(/^[^<>]*$/, `${label} contains invalid characters.`);

// ── Exported schemas ────────────────────────────────────────────────────────

export const registerSchema = z.object({
  organisationName: nameSchema('Organisation name', 200),
  fullName: nameSchema('Your name', 200),
  email: emailSchema,
  password: passwordSchema,
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Password is required.').max(128),
});

export const forgotPasswordSchema = z.object({
  email: emailSchema,
});

export const resetPasswordSchema = z
  .object({
    token: z.string().min(1, 'Token is required.'),
    newPassword: passwordSchema,
    confirmPassword: z.string().min(1, 'Please confirm your password.'),
  })
  .refine((data) => data.newPassword === data.confirmPassword, {
    message: 'Passwords do not match.',
    path: ['confirmPassword'],
  });

export const resendVerificationSchema = z.object({
  email: emailSchema,
});

export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type ForgotPasswordInput = z.infer<typeof forgotPasswordSchema>;
export type ResetPasswordInput = z.infer<typeof resetPasswordSchema>;
export type ResendVerificationInput = z.infer<typeof resendVerificationSchema>;
